import {
    AfterViewInit,
    Component,
    OnDestroy,
    ViewChild,
    ChangeDetectorRef,
    ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
    MatPaginator,
    MatPaginatorModule,
    PageEvent
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { SearchInputWithDebouncingComponent } from '../../../ui-components/search-input-with-debouncing/search-input-with-debouncing.component';
import { BufferProgressBarComponent } from '../../../ui-components/buffer-progress-bar/buffer-progress-bar.component';
import { InvoiceDto, InvoiceService } from '../services/invoice.service';
import { TranslatePipe } from '@ngx-translate/core';

type InvoiceRow = {
    id: number;
    user: string;
    price: number;
    issueDate: string;
    dueDate: string;
    status: string;
};

@Component({
    selector: 'app-all-invoices-list',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatCardModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatTooltipModule,
        MatIconModule,
        BufferProgressBarComponent,
        SearchInputWithDebouncingComponent,
        RouterLink,
        TranslatePipe
    ],
    templateUrl: './all-invoices-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './all-invoices-list.component.scss'
})
export class AllInvoicesListComponent implements AfterViewInit, OnDestroy {
    displayedColumns: string[] = [
        'id',
        'user',
        'price',
        'issueDate',
        'dueDate',
        'status',
        'action'
    ];

    dataSource = new MatTableDataSource<InvoiceRow>([]);
    total = 0;
    pageSize = 10;
    isLoading = false;

    private destroy$ = new Subject<void>();
    private searchText = '';

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        private invoiceService: InvoiceService,
        private router: Router,
        private cd: ChangeDetectorRef
    ) {}

    ngAfterViewInit(): void {
        this.paginator.pageSize = this.pageSize;
        this.dataSource.sort = this.sort;
        this.loadInvoices(0, this.paginator.pageSize);
        this.paginator.page
            .pipe(takeUntil(this.destroy$))
            .subscribe((e: PageEvent) =>
                this.loadInvoices(e.pageIndex, e.pageSize)
            );
        this.cd.detectChanges();
    }

    onSearch(q: string) {
        this.searchText = q.trim();
        this.paginator.firstPage();
        this.loadInvoices(0, this.paginator.pageSize);
    }

    private loadInvoices(pageIndex: number, pageSize: number) {
        this.isLoading = true;

        this.invoiceService
            .getInvoices({
                page: pageIndex + 1,
                pageSize,
                searchText: this.searchText
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: res => {
                    this.dataSource.data = res.items;
                    this.total = res.total;
                    this.isLoading = false;
                },
                error: () => {
                    this.dataSource.data = [];
                    this.total = 0;
                    this.isLoading = false;
                }
            });
    }

    onView(row: InvoiceDto) {
        // replace with actual navigation when invoice detail page is ready
        this.router.navigate(['/invoices/invoice-details', row.id]);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
